// ────────────────────────────────────────────────────────────────────────────
// Investments page (Slice 9 rebuild).
//
// The page is reorganized around the IRS caps themselves — four "Cap"
// cards: 402(g), 415(c), IRA, and Uncapped. Each card holds the rules whose
// `taxTreatment` consumes that cap, shows a per-cap headroom indicator, and
// offers a one-click "Max this out for me" affordance that pre-fills a rule
// for the remaining headroom and opens the editor.
//
// The editor is cap-aware: when invoked from a cap, it pre-scopes the
// available tax-treatment options to those valid for that cap, shows a
// live "Cap headroom remaining" hint, and warns inline when an amount would
// exceed the cap (with a link to a different cap).
//
// A small "Quick setup" callout sits above the cap cards for first-time
// users — it disappears as soon as any retirement-targeted rule exists.
//
// Per-account view + RSU vests sections are unchanged from Slice 5.
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { PrivacyBanner } from '../components/PrivacyBanner';
import { MoneyInput } from '../components/MoneyInput';
import { PctInput } from '../components/PctInput';
import { GlossaryTerm } from '../components/GlossaryTerm';
import { Banner, Chip, EmptyState, Money, Toggle } from '../ui';
import { fmtMoney } from '../utils/format';
import { uuid } from '../utils/uuid';
import { accountTouches } from '../engine/projection';
import {
  applyElectiveCaps,
  resolveRuleAnnual,
} from '../engine/contributions';
import {
  electiveDeferralLimit,
  annualAdditionsLimit,
  iraContributionLimit,
} from '../tax/data';
import type {
  Account,
  Compensation,
  ContributionAccountTarget,
  ContributionMode,
  ContributionRule,
  ContributionSchedule,
  ContributionTaxTreatment,
  PayMonth,
  RsuVestRow,
} from '../types';

// ── Constants & helpers ────────────────────────────────────────────────────

const DEFAULT_COMPENSATION: Compensation = {
  baseAnnual: 0,
  bonusAnnual: 0,
  bonusMonth: 2,
};

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const TAX_TREATMENT_LABELS: Record<ContributionTaxTreatment, string> = {
  pretax: 'Traditional 401(k)',
  roth_401k: 'Roth 401(k)',
  after_tax_401k: 'After-tax 401(k)',
  mega_backdoor_to_roth: 'Mega backdoor → Roth',
  roth_ira: 'Roth IRA',
  after_tax_brokerage: 'After-tax brokerage',
};

/** Glossary id corresponding to each tax treatment. Used to make the rule-
 *  row chips into "click to learn what this means" affordances. */
const TAX_TREATMENT_GLOSSARY: Record<ContributionTaxTreatment, string> = {
  pretax: 'pretax_401k',
  roth_401k: 'roth_401k',
  after_tax_401k: 'after_tax_401k',
  mega_backdoor_to_roth: 'mega_backdoor',
  roth_ira: 'roth_ira',
  after_tax_brokerage: 'taxable_brokerage',
};

const TAX_TREATMENT_TONES: Record<
  ContributionTaxTreatment,
  'accent' | 'pos' | 'warning' | 'muted' | 'neutral'
> = {
  pretax: 'accent',          // blue-ish — counts vs. 402(g)
  roth_401k: 'pos',          // green — Roth treatment
  after_tax_401k: 'warning', // amber — uses 415(c) headroom
  mega_backdoor_to_roth: 'pos', // also green — lands in Roth
  roth_ira: 'pos',
  after_tax_brokerage: 'muted',
};

/** A cap groups the contribution rules that consume the same IRS cap.
 *  Drives the four cards on the page and the cap-aware editor scope. */
type CapId = '402g' | '415c' | 'ira' | 'uncapped';

/** Tax treatments that "live in" each cap. The editor uses this to
 *  pre-scope the dropdown when adding/editing from a cap. */
const CAP_TREATMENTS: Record<CapId, ContributionTaxTreatment[]> = {
  '402g': ['pretax', 'roth_401k'],
  '415c': ['after_tax_401k', 'mega_backdoor_to_roth'],
  ira: ['roth_ira'],
  uncapped: ['after_tax_brokerage'],
};

/** Which cap a tax treatment falls into. Reverse map of CAP_TREATMENTS. */
function capForTreatment(tt: ContributionTaxTreatment | undefined): CapId {
  if (!tt) return 'uncapped';
  for (const cap of ['402g', '415c', 'ira', 'uncapped'] as CapId[]) {
    if (CAP_TREATMENTS[cap].includes(tt)) return cap;
  }
  return 'uncapped';
}

/** Friendly per-treatment cap-impact hint shown next to each option in the
 *  cap-aware editor dropdown. */
const TAX_TREATMENT_CAP_HINT: Record<ContributionTaxTreatment, string> = {
  pretax: 'counts against 402(g)',
  roth_401k: 'counts against 402(g)',
  after_tax_401k: 'counts against 415(c) only',
  mega_backdoor_to_roth: 'counts against 415(c), lands in Roth',
  roth_ira: 'counts against IRA limit',
  after_tax_brokerage: 'no IRS cap',
};

interface ProgressLineProps {
  label: ReactNode;
  intended: number;
  cap: number;
  /** Optional sub-note rendered to the right of the cap (e.g. "+ catch-up"). */
  capNote?: string;
  /** Optional click handler — turns the line into a clickable region that
   *  scrolls to the matching cap card below. */
  onClick?: () => void;
}

/** A labelled progress bar: "$X intended / $Y cap — Z% utilized". Tonal
 *  feedback shifts from accent (room) to pos (right at) to warning (over). */
function ProgressLine({ label, intended, cap, capNote, onClick }: ProgressLineProps) {
  const pct = cap > 0 ? Math.min(intended / cap, 1) : 0;
  const overage = Math.max(0, intended - cap);
  const isOver = overage > 0.5;
  const isMaxed = !isOver && cap > 0 && intended >= cap - 0.5;

  const barColor = isOver
    ? 'bg-amber-500'
    : isMaxed
      ? 'bg-pos-500'
      : 'bg-accent-500';

  const wrapClass = onClick
    ? 'space-y-1 cursor-pointer rounded-lg p-1 -m-1 transition hover:bg-ink-100/60 dark:hover:bg-ink-700/40'
    : 'space-y-1';

  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <div className="font-medium text-ink-700 dark:text-ink-100">{label}</div>
        <div className="font-mono tabular-nums text-ink-500 dark:text-ink-300">
          {fmtMoney(intended)} <span className="text-ink-400">/</span>{' '}
          {fmtMoney(cap)}
          {capNote && (
            <span className="ml-1 text-[10px] text-ink-400">{capNote}</span>
          )}
        </div>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100 dark:bg-ink-700"
        aria-valuenow={Math.round(pct * 100)}
        aria-valuemax={100}
        role="progressbar"
      >
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${(isOver ? 1 : pct) * 100}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-ink-500 dark:text-ink-300">
          {(pct * 100).toFixed(0)}% utilized
        </span>
        {isOver ? (
          <Chip tone="warning" size="sm">
            ⚠ {fmtMoney(overage)} over — auto-trimmed by engine
          </Chip>
        ) : isMaxed ? (
          <Chip tone="pos" size="sm">
            🎉 Maxed out
          </Chip>
        ) : pct >= 0.9 ? (
          <Chip tone="accent" size="sm">
            Almost there
          </Chip>
        ) : pct < 0.25 ? (
          <Chip tone="muted" size="sm">
            Plenty of room
          </Chip>
        ) : null}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${wrapClass} block w-full text-left`}
        aria-label="Scroll to this cap below"
      >
        {inner}
      </button>
    );
  }
  return <div className={wrapClass}>{inner}</div>;
}

// ── Page ───────────────────────────────────────────────────────────────────

export function InvestmentsPage() {
  const {
    assumptions,
    saveAssumptions,
    accounts,
    snapshots,
    currentBalances,
    projection,
    saveAccount,
  } = useStore();

  const rules = useMemo<ContributionRule[]>(
    () =>
      [...(assumptions.contributionRules ?? [])].sort(
        (a, b) => a.order - b.order || a.id.localeCompare(b.id),
      ),
    [assumptions.contributionRules],
  );

  const comp = assumptions.compensation ?? {
    ...DEFAULT_COMPENSATION,
    baseAnnual: assumptions.grossAnnualSalary,
  };
  const age = assumptions.currentAge;

  // Cap-aware snapshot for the user's current age (and current rules).
  const capResult = useMemo(
    () => applyElectiveCaps(rules, comp, age),
    [rules, comp, age],
  );

  // Per-rule intended dollars (pre-cap). Used both for cap progress lines and
  // for the "intended vs. actual" hint on each rule row.
  const intendedByRule = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rules) m.set(r.id, resolveRuleAnnual(r, comp));
    return m;
  }, [rules, comp]);

  // ── Cap-class summary aggregates ────────────────────────────────────────
  const intended402g = useMemo(
    () =>
      rules
        .filter(r => r.taxTreatment === 'pretax' || r.taxTreatment === 'roth_401k')
        .reduce((s, r) => s + (intendedByRule.get(r.id) ?? 0), 0),
    [rules, intendedByRule],
  );
  const intended415c = useMemo(() => {
    // 415(c) total annual additions: 402(g) employee deferrals + after-tax
    // caps + employer match (mirrors caps.ts's tally).
    const employee401k = intended402g;
    const afterTax = rules
      .filter(
        r =>
          r.taxTreatment === 'after_tax_401k' ||
          r.taxTreatment === 'mega_backdoor_to_roth',
      )
      .reduce((s, r) => s + (intendedByRule.get(r.id) ?? 0), 0);
    return employee401k + afterTax + capResult.employerMatch;
  }, [rules, intendedByRule, intended402g, capResult.employerMatch]);
  const intendedIra = useMemo(
    () =>
      rules
        .filter(r => r.taxTreatment === 'roth_ira')
        .reduce((s, r) => s + (intendedByRule.get(r.id) ?? 0), 0),
    [rules, intendedByRule],
  );

  const limit402g = electiveDeferralLimit(age);
  const limit415c = annualAdditionsLimit(age);
  const limitIra = iraContributionLimit(age);

  // Headroom remaining in each cap, given current rules. Used by the
  // "Max this out for me" affordances and by the cap-aware editor hint.
  const headroom402g = Math.max(0, limit402g - intended402g);
  const headroom415c = Math.max(0, limit415c - intended415c);
  const headroomIra = Math.max(0, limitIra - intendedIra);

  // Catch-up annotations: tell the user WHY their cap is what it is.
  const catchUpNote =
    age >= 60 && age <= 63
      ? '+ super-catch-up'
      : age >= 50
        ? '+ catch-up'
        : undefined;

  // ── Group rules by cap for the four-card layout ──────────────────────
  const rulesByCap = useMemo(() => {
    const m: Record<CapId, ContributionRule[]> = {
      '402g': [],
      '415c': [],
      ira: [],
      uncapped: [],
    };
    for (const r of rules) m[capForTreatment(r.taxTreatment)].push(r);
    return m;
  }, [rules]);

  // ── Rules CRUD state ────────────────────────────────────────────────────
  // The editor is cap-aware: when opened, it remembers which cap it
  // was opened from so it can scope the tax-treatment dropdown and surface
  // the matching headroom hint.
  const [editing, setEditing] = useState<ContributionRule | null>(null);
  const [editorCap, setEditorCap] = useState<CapId | null>(null);
  const [showNew, setShowNew] = useState(false);

  function openEditorForRule(rule: ContributionRule) {
    setShowNew(false);
    setEditing(rule);
    setEditorCap(capForTreatment(rule.taxTreatment));
  }

  function openNewInCap(cap: CapId, prefill?: Partial<ContributionRule>) {
    setEditing(null);
    setEditorCap(cap);
    setShowNew(true);
    setPendingPrefill(prefill ?? null);
  }

  function closeEditor() {
    setEditing(null);
    setShowNew(false);
    setEditorCap(null);
    setPendingPrefill(null);
  }

  // Prefill carrier for the editor when opened via "Max this out for me".
  // Kept as state so the editor mounts with the right starting draft, then
  // the user can confirm / tweak before saving.
  const [pendingPrefill, setPendingPrefill] =
    useState<Partial<ContributionRule> | null>(null);

  async function handleSaveRule(rule: ContributionRule) {
    const existing = assumptions.contributionRules ?? [];
    const idx = existing.findIndex(r => r.id === rule.id);
    const next = idx >= 0
      ? existing.map(r => (r.id === rule.id ? rule : r))
      : [...existing, rule];
    await saveAssumptions({
      ...assumptions,
      contributionRules: next,
      updatedAt: new Date().toISOString(),
    });
    closeEditor();
  }

  async function handleDeleteRule(id: string) {
    const existing = assumptions.contributionRules ?? [];
    const filtered = existing
      .filter(r => r.id !== id)
      .sort((a, b) => a.order - b.order)
      .map((r, i) => ({ ...r, order: i }));
    await saveAssumptions({
      ...assumptions,
      contributionRules: filtered,
      updatedAt: new Date().toISOString(),
    });
    closeEditor();
  }

  /** Bulk add (used by Quick Setup). All three rules go in via a single
   *  saveAssumptions write so the polled refresh sees one consistent state. */
  async function handleBulkAddRules(toAdd: ContributionRule[]) {
    const existing = assumptions.contributionRules ?? [];
    const next = [...existing, ...toAdd];
    await saveAssumptions({
      ...assumptions,
      contributionRules: next,
      updatedAt: new Date().toISOString(),
    });
  }

  // ── Per-account view: investable accounts only, archived hidden ─────────
  const investableAccounts = useMemo(
    () => accounts.filter(a => a.bucket !== null && !a.archived),
    [accounts],
  );

  const touchesByAccount = useMemo(
    () => accountTouches(snapshots, investableAccounts),
    [snapshots, investableAccounts],
  );

  // Per-account: which (post-cap) rules feed it, summed.
  const perAccountContrib = useMemo(() => {
    const m = new Map<string, { intended: number; actual: number }>();
    for (const a of investableAccounts) m.set(a.id, { intended: 0, actual: 0 });
    for (const r of rules) {
      if (r.target.kind !== 'account') continue;
      const slot = m.get(r.target.accountId);
      if (!slot) continue;
      slot.intended += intendedByRule.get(r.id) ?? 0;
      slot.actual += capResult.byRule.get(r.id) ?? 0;
    }
    return m;
  }, [investableAccounts, rules, intendedByRule, capResult]);

  // First projection year — for "projected end of year 1" per bucket.
  const year1 = projection.years[0];

  // ── RSU vests ───────────────────────────────────────────────────────────
  const rsuVests = comp.rsuVests ?? [];

  async function setComp(next: Compensation) {
    await saveAssumptions({
      ...assumptions,
      compensation: next,
      updatedAt: new Date().toISOString(),
    });
  }

  // ── Quick-setup callout visibility ──────────────────────────────────────
  // Hidden as soon as ANY retirement-targeted rule exists — i.e. the user
  // has already started authoring their stack and doesn't need the prompt.
  const hasAnyRetirementRule = rules.some(
    r =>
      r.taxTreatment === 'pretax' ||
      r.taxTreatment === 'roth_401k' ||
      r.taxTreatment === 'after_tax_401k' ||
      r.taxTreatment === 'mega_backdoor_to_roth' ||
      r.taxTreatment === 'roth_ira',
  );
  const [quickSetupOpen, setQuickSetupOpen] = useState(false);

  // ── cap-card scroll refs (clickable summary bars at top scroll here) ─
  const capRefs: Record<CapId, React.RefObject<HTMLDivElement>> = {
    '402g': useRef<HTMLDivElement>(null),
    '415c': useRef<HTMLDivElement>(null),
    ira: useRef<HTMLDivElement>(null),
    uncapped: useRef<HTMLDivElement>(null),
  };
  function scrollToCap(s: CapId) {
    capRefs[s].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // cap-card metadata, computed once per render.
  const capMeta: Record<
    CapId,
    {
      title: ReactNode;
      subtitle: string;
      cap: number | null;
      intended: number;
      headroom: number;
      capNote: string | undefined;
      defaultTreatment: ContributionTaxTreatment | undefined;
      defaultSchedule: ContributionSchedule;
    }
  > = {
    '402g': {
      title: (
        <>
          <GlossaryTerm term="irs_402g">402(g)</GlossaryTerm> — Employee 401(k)
          deferral
        </>
      ),
      subtitle: 'Pretax + Roth 401(k) employee deferrals (combined)',
      cap: limit402g,
      intended: intended402g,
      headroom: headroom402g,
      capNote: catchUpNote,
      defaultTreatment: 'pretax',
      defaultSchedule: { kind: 'lump_sum', month: 1 },
    },
    '415c': {
      title: (
        <>
          <GlossaryTerm term="irs_415c">415(c)</GlossaryTerm> — Total annual
          additions
        </>
      ),
      subtitle:
        'Combined 401(k) ceiling — includes employee deferrals, employer match, and after-tax / mega-backdoor.',
      cap: limit415c,
      intended: intended415c,
      headroom: headroom415c,
      capNote: catchUpNote,
      defaultTreatment: 'mega_backdoor_to_roth',
      defaultSchedule: { kind: 'lump_sum', month: 1 },
    },
    ira: {
      title: (
        <>
          <GlossaryTerm term="ira_limit">IRA</GlossaryTerm> — Roth / Traditional
          IRA
        </>
      ),
      subtitle: 'Outside the 401(k) — separate IRS limit.',
      cap: limitIra,
      intended: intendedIra,
      headroom: headroomIra,
      capNote: age >= 50 ? '+ catch-up' : undefined,
      defaultTreatment: 'roth_ira',
      defaultSchedule: { kind: 'monthly' },
    },
    uncapped: {
      title: <>Uncapped — Brokerage / HYSA</>,
      subtitle: 'After-tax savings outside the IRS contribution limits.',
      cap: null,
      intended: rulesByCap.uncapped.reduce(
        (s, r) => s + (intendedByRule.get(r.id) ?? 0),
        0,
      ),
      headroom: Number.POSITIVE_INFINITY,
      capNote: undefined,
      defaultTreatment: 'after_tax_brokerage',
      defaultSchedule: { kind: 'monthly' },
    },
  };

  /** Compose a "Max this out" action for a given cap. Builds a prefill
   *  rule for the remaining headroom and opens the editor (so the user can
   *  confirm before committing). Returns null if there's no headroom or the
   *  cap is uncapped (caller renders a different affordance). */
  function maxOutPrefill(cap: CapId): Partial<ContributionRule> | null {
    const meta = capMeta[cap];
    if (cap === 'uncapped') return null;
    if (meta.headroom <= 0.5) return null;
    return {
      name: defaultRuleNameFor(cap),
      target: { kind: 'bucket', bucket: 'retirement' },
      mode: { kind: 'fixed_dollar', annual: Math.round(meta.headroom) },
      schedule: meta.defaultSchedule,
      taxTreatment: meta.defaultTreatment,
      enabled: true,
    };
  }

  function defaultRuleNameFor(cap: CapId): string {
    switch (cap) {
      case '402g': return 'Pretax 401(k)';
      case '415c': return 'Mega backdoor 401(k) → Roth';
      case 'ira': return 'Roth IRA';
      case 'uncapped': return 'Brokerage auto-invest';
    }
  }

  // ── Quick-setup builders ────────────────────────────────────────────────
  /** Pre-baked three-rule starter set: max 402(g), max 415(c) (after match
   *  + pretax), max IRA. Computed live so the values track the user's age
   *  and existing comp/match. Rules are returned with fresh ids so they
   *  can be tossed into saveAssumptions. */
  function buildQuickSetupRules(): ContributionRule[] {
    const baseOrder = rules.length;
    const out: ContributionRule[] = [];

    // 402(g): full pretax cap, lump-Jan.
    if (limit402g > 0) {
      out.push({
        id: uuid(),
        name: 'Pretax 401(k) (max)',
        target: { kind: 'bucket', bucket: 'retirement' },
        mode: { kind: 'fixed_dollar', annual: limit402g },
        schedule: { kind: 'lump_sum', month: 1 },
        taxTreatment: 'pretax',
        enabled: true,
        order: baseOrder + out.length,
      });
    }

    // 415(c): the leftover after pretax + employer match.
    const projectedEmployerMatch = (() => {
      // Re-run the match calc against the proposed pretax to keep numbers honest.
      if (!comp.employer401kMatchPct || !comp.employer401kMatchCapPct) return 0;
      const eligible = comp.baseAnnual + comp.bonusAnnual;
      const matchableEmployee = Math.min(
        limit402g,
        eligible * comp.employer401kMatchCapPct,
      );
      return matchableEmployee * (comp.employer401kMatchPct / comp.employer401kMatchCapPct);
    })();
    const megaHeadroom = Math.max(0, limit415c - limit402g - projectedEmployerMatch);
    if (megaHeadroom > 0.5) {
      out.push({
        id: uuid(),
        name: 'Mega backdoor 401(k) → Roth (max)',
        target: { kind: 'bucket', bucket: 'retirement' },
        mode: { kind: 'fixed_dollar', annual: Math.round(megaHeadroom) },
        schedule: { kind: 'lump_sum', month: 1 },
        taxTreatment: 'mega_backdoor_to_roth',
        enabled: true,
        order: baseOrder + out.length,
      });
    }

    // IRA: full Roth IRA cap, monthly.
    if (limitIra > 0) {
      out.push({
        id: uuid(),
        name: 'Roth IRA (max)',
        target: { kind: 'bucket', bucket: 'retirement' },
        mode: { kind: 'fixed_dollar', annual: limitIra },
        schedule: { kind: 'monthly' },
        taxTreatment: 'roth_ira',
        enabled: true,
        order: baseOrder + out.length,
      });
    }

    return out;
  }

  return (
    <>
      <TopBar
        title="Investments"
        subtitle="Contribution rules, cap awareness, per-account view"
      />
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-4 pb-nav">
        <PrivacyBanner />

        {/* ── Section A: cap awareness summary (with click-to-scroll) ── */}
        <section className="card card-dark overflow-hidden">
          <div className="border-b border-ink-100 px-4 py-2 dark:border-ink-700">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              2026 IRS contribution limits
            </div>
            <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">
              Adjust your stack below — each cap shows its own cap. Computed
              for age {age}
              {catchUpNote ? ` (${catchUpNote.replace('+ ', '')} applies)` : ''}.
            </div>
          </div>
          <div className="space-y-4 p-4">
            <ProgressLine
              label={
                <>
                  <GlossaryTerm term="401k">401(k)</GlossaryTerm> employee deferral —{' '}
                  <GlossaryTerm term="irs_402g">402(g)</GlossaryTerm>
                </>
              }
              intended={intended402g}
              cap={limit402g}
              capNote={catchUpNote}
              onClick={() => scrollToCap('402g')}
            />
            <ProgressLine
              label={
                <>
                  Total annual additions —{' '}
                  <GlossaryTerm term="irs_415c">415(c)</GlossaryTerm>
                </>
              }
              intended={intended415c}
              cap={limit415c}
              capNote={catchUpNote}
              onClick={() => scrollToCap('415c')}
            />
            <ProgressLine
              label={
                <>
                  <GlossaryTerm term="traditional_ira">IRA</GlossaryTerm> /{' '}
                  <GlossaryTerm term="roth_ira">Roth IRA</GlossaryTerm>
                </>
              }
              intended={intendedIra}
              cap={limitIra}
              capNote={age >= 50 ? '+ catch-up' : undefined}
              onClick={() => scrollToCap('ira')}
            />
            {capResult.employerMatch > 0 && (
              <div className="rounded-lg bg-ink-50/60 px-3 py-2 text-[11px] text-ink-500 dark:bg-ink-800/50 dark:text-ink-300">
                Employer 401(k) match modeled at {fmtMoney(capResult.employerMatch)}/yr
                — counts against{' '}
                <GlossaryTerm term="irs_415c">415(c)</GlossaryTerm>, not{' '}
                <GlossaryTerm term="irs_402g">402(g)</GlossaryTerm>.
              </div>
            )}
            {capResult.cappedByRule.size > 0 && (
              <Banner
                tone="warning"
                title="Some rules are over the cap"
              >
                The engine auto-trimmed{' '}
                {Array.from(capResult.cappedByRule.entries())
                  .map(([id, over]) => {
                    const r = rules.find(x => x.id === id);
                    return r ? `${r.name} (${fmtMoney(over)})` : null;
                  })
                  .filter(Boolean)
                  .join(', ')}
                . If you want the spillover to land in Roth, add a{' '}
                <GlossaryTerm term="mega_backdoor">mega backdoor</GlossaryTerm>{' '}
                rule below.
              </Banner>
            )}
          </div>
        </section>

        {/* ── Section A2: Quick-setup callout (first-time users) ── */}
        {!hasAnyRetirementRule && (
          <section className="card card-dark overflow-hidden">
            <div className="flex flex-wrap items-start gap-3 p-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-lg dark:bg-accent-700/30">
                ⚡
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">
                  Quick setup — first time?
                </div>
                <div className="mt-1 text-[12px] text-ink-500 dark:text-ink-300">
                  Cairn can set up the three most common retirement contributions
                  in one click:
                </div>
                <ul className="mt-2 space-y-0.5 text-[12px] text-ink-700 dark:text-ink-100">
                  <li>
                    • Max your <GlossaryTerm term="pretax_401k">401(k)</GlossaryTerm>{' '}
                    ({fmtMoney(limit402g)})
                  </li>
                  <li>
                    • Max <GlossaryTerm term="mega_backdoor">mega backdoor</GlossaryTerm>{' '}
                    (~{fmtMoney(Math.max(0, limit415c - limit402g - capResult.employerMatch))})
                  </li>
                  <li>
                    • Max <GlossaryTerm term="roth_ira">Roth IRA</GlossaryTerm>{' '}
                    ({fmtMoney(limitIra)})
                  </li>
                </ul>
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-primary text-xs"
                    onClick={() => setQuickSetupOpen(true)}
                  >
                    Set up all three for me
                  </button>
                </div>
              </div>
            </div>
            {quickSetupOpen && (
              <QuickSetupConfirm
                rules={buildQuickSetupRules()}
                onCancel={() => setQuickSetupOpen(false)}
                onConfirm={async toAdd => {
                  await handleBulkAddRules(toAdd);
                  setQuickSetupOpen(false);
                }}
              />
            )}
          </section>
        )}

        {/* ── Section B: four cap cards ── */}
        {(['402g', '415c', 'ira', 'uncapped'] as CapId[]).map(cap => {
          const meta = capMeta[cap];
          const capRules = rulesByCap[cap];
          const showMaxOut = cap !== 'uncapped' && meta.headroom > 0.5;
          const employerMatchLine =
            cap === '415c' && capResult.employerMatch > 0;

          return (
            <section
              key={cap}
              ref={capRefs[cap]}
              className="card card-dark overflow-hidden scroll-mt-4"
            >
              <div className="flex flex-wrap items-start gap-3 border-b border-ink-100 px-4 py-3 dark:border-ink-700">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">
                    {meta.title}
                  </div>
                  <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">
                    {meta.subtitle}
                  </div>
                </div>
                {meta.cap !== null ? (
                  <CapHeadroomChip
                    intended={meta.intended}
                    cap={meta.cap}
                    capNote={meta.capNote}
                  />
                ) : (
                  <Chip tone="muted" size="sm">
                    No IRS cap
                  </Chip>
                )}
              </div>

              {/* Per-cap mini progress bar (only for capped caps). */}
              {meta.cap !== null && (
                <div className="border-b border-ink-100 px-4 py-3 dark:border-ink-700">
                  <ProgressLine
                    label={
                      <span className="text-[11px] uppercase tracking-wide text-ink-500 dark:text-ink-300">
                        This cap
                      </span>
                    }
                    intended={meta.intended}
                    cap={meta.cap}
                    capNote={meta.capNote}
                  />
                </div>
              )}

              {/* Employer match informational row (415(c) only). */}
              {employerMatchLine && (
                <div className="border-b border-ink-100 px-4 py-3 dark:border-ink-700">
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pos-100 text-[12px] dark:bg-pos-700/30">
                      🏢
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink-900 dark:text-ink-50">
                        Employer match — {fmtMoney(capResult.employerMatch)}/yr
                      </div>
                      <div className="text-[11px] text-ink-500 dark:text-ink-300">
                        {comp.employer401kMatchPct !== undefined &&
                        comp.employer401kMatchCapPct !== undefined
                          ? `${(comp.employer401kMatchPct * 100).toFixed(2)}% of pay matched up to ${(comp.employer401kMatchCapPct * 100).toFixed(2)}% deferral.`
                          : 'From compensation settings.'}{' '}
                        Informational — not from your cash.
                      </div>
                    </div>
                    <a
                      href="#assumptions"
                      className="btn btn-ghost text-xs"
                    >
                      Edit in Compensation →
                    </a>
                  </div>
                </div>
              )}

              {/* Rules list. */}
              {capRules.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon={cap === 'uncapped' ? '💰' : '🪜'}
                    title={`No rules in this cap yet`}
                    description={
                      cap === 'uncapped'
                        ? 'Add a rule for after-tax brokerage or HYSA contributions.'
                        : 'Add a rule below — or click "Max this out for me" to fill the cap in one click.'
                    }
                  />
                </div>
              ) : (
                <ul className="divide-y divide-ink-100 dark:divide-ink-700">
                  {capRules.map(r => (
                    <RuleRow
                      key={r.id}
                      rule={r}
                      accounts={accounts}
                      intended={intendedByRule.get(r.id) ?? 0}
                      actual={capResult.byRule.get(r.id) ?? 0}
                      capped={capResult.cappedByRule.get(r.id) ?? 0}
                      capReason={capResult.capReasonByRule.get(r.id) ?? null}
                      onEdit={() => openEditorForRule(r)}
                    />
                  ))}
                </ul>
              )}

              {/* cap-scoped affordance: "Max this out for me" or "+ Add". */}
              {!(editing && editorCap === cap) && !(showNew && editorCap === cap) && (
                <div className="flex flex-wrap gap-2 border-t border-ink-100 px-4 py-3 dark:border-ink-700">
                  {showMaxOut && (
                    <button
                      type="button"
                      className="btn btn-primary text-xs"
                      onClick={() => {
                        const prefill = maxOutPrefill(cap);
                        if (prefill) openNewInCap(cap, prefill);
                      }}
                    >
                      {meta.intended === 0
                        ? `Max this out for me (${fmtMoney(meta.headroom)})`
                        : `Fill the rest of this cap (${fmtMoney(meta.headroom)} remaining)`}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost text-xs"
                    onClick={() => openNewInCap(cap)}
                  >
                    {cap === 'uncapped' ? '+ Add brokerage rule' : '+ Add a contribution to this cap'}
                  </button>
                </div>
              )}

              {/* In-card editor (mounts under the cap it was opened from). */}
              {editorCap === cap && (editing || showNew) && (
                <div className="border-t border-ink-100 p-4 dark:border-ink-700">
                  <RuleEditor
                    key={editing?.id ?? 'new'}
                    rule={
                      editing ?? blankRuleForCap(rules.length, cap, pendingPrefill)
                    }
                    cap={cap}
                    accounts={accounts}
                    isNew={!editing}
                    headroomBefore={meta.headroom + (editing ? (intendedByRule.get(editing.id) ?? 0) : 0)}
                    capLabel={meta.cap !== null ? meta.cap : null}
                    onCancel={closeEditor}
                    onSave={handleSaveRule}
                    onDelete={
                      editing
                        ? () => {
                            if (
                              confirm(
                                `Delete contribution rule "${editing.name}"? The engine will stop allocating these dollars next projection.`,
                              )
                            ) {
                              void handleDeleteRule(editing.id);
                            }
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </section>
          );
        })}

        {/* ── Section C: per-account investment view ── */}
        <section className="card card-dark overflow-hidden">
          <div className="border-b border-ink-100 px-4 py-2 dark:border-ink-700">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              Per-account investment view
            </div>
            <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">
              Investable accounts (HYSA, taxable, retirement) — current balance,
              what feeds them, projected end of year 1.
            </div>
          </div>
          {investableAccounts.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon="🏦"
                title="No investable accounts yet"
                description="Add HYSA, taxable, or retirement accounts on the Accounts page to see them here."
              />
            </div>
          ) : (
            <ul className="divide-y divide-ink-100 dark:divide-ink-700">
              {investableAccounts.map(a => (
                <AccountInvestmentRow
                  key={a.id}
                  account={a}
                  balance={currentBalances[a.id] ?? 0}
                  rules={rules.filter(
                    r =>
                      r.target.kind === 'account' &&
                      r.target.accountId === a.id,
                  )}
                  intended={perAccountContrib.get(a.id)?.intended ?? 0}
                  actual={perAccountContrib.get(a.id)?.actual ?? 0}
                  touches={touchesByAccount[a.id] ?? []}
                  year1End={
                    a.bucket && year1
                      ? year1[a.bucket].end
                      : null
                  }
                  onSetTaxSleeve={async sleeve => {
                    await saveAccount({ ...a, taxSleeve: sleeve });
                  }}
                />
              ))}
            </ul>
          )}
        </section>

        {/* ── Section D: RSU vests ── */}
        <section className="card card-dark overflow-hidden">
          <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2 dark:border-ink-700">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
                RSU vests
              </div>
              <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">
                Equity grants vesting on a schedule. Treated as supplemental
                ordinary wage income at vest.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-ghost text-xs"
              onClick={() => {
                const next: RsuVestRow = {
                  yearOffset: 1,
                  month: 2,
                  grossValue: 0,
                };
                void setComp({
                  ...comp,
                  rsuVests: [...rsuVests, next],
                });
              }}
            >
              + Add vest
            </button>
          </div>
          {rsuVests.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon="📈"
                title="No RSU vests modeled"
                description="Add one if your compensation includes equity grants vesting on a schedule."
              />
            </div>
          ) : (
            <ul className="divide-y divide-ink-100 dark:divide-ink-700">
              {rsuVests.map((v, idx) => (
                <RsuVestRowEditor
                  key={idx}
                  vest={v}
                  startingYear={assumptions.startingCalendarYear}
                  onChange={next => {
                    const updated = rsuVests.map((x, i) => (i === idx ? next : x));
                    void setComp({ ...comp, rsuVests: updated });
                  }}
                  onRemove={() => {
                    const updated = rsuVests.filter((_, i) => i !== idx);
                    void setComp({
                      ...comp,
                      rsuVests: updated.length > 0 ? updated : undefined,
                    });
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function CapHeadroomChip({
  intended,
  cap,
  capNote,
}: {
  intended: number;
  cap: number;
  capNote?: string;
}) {
  if (cap <= 0) return null;
  const headroom = Math.max(0, cap - intended);
  const overage = Math.max(0, intended - cap);
  if (overage > 0.5) {
    return (
      <Chip tone="warning" size="sm">
        ⚠ {fmtMoney(overage)} over cap
      </Chip>
    );
  }
  if (headroom <= 0.5) {
    return (
      <Chip tone="pos" size="sm">
        🎉 Maxed out
      </Chip>
    );
  }
  return (
    <Chip tone="accent" size="sm">
      {fmtMoney(headroom)} headroom{capNote ? ` (${capNote.replace('+ ', '')})` : ''}
    </Chip>
  );
}

function RuleRow({
  rule,
  accounts,
  intended,
  actual,
  capped,
  capReason,
  onEdit,
}: {
  rule: ContributionRule;
  accounts: Account[];
  intended: number;
  actual: number;
  capped: number;
  capReason: '402g' | '415c' | 'ira' | null;
  onEdit: () => void;
}) {
  const targetLabel =
    rule.target.kind === 'bucket'
      ? `→ ${capitalize(rule.target.bucket)} bucket`
      : `→ ${accounts.find(a => a.id === (rule.target as Extract<ContributionAccountTarget, { kind: 'account' }>).accountId)?.name ?? '(unknown account)'}`;
  const modeLabel =
    rule.mode.kind === 'fixed_dollar'
      ? `${fmtMoney(rule.mode.annual)}/yr fixed`
      : `${(rule.mode.pct * 100).toFixed(2)}% of pay${rule.mode.baseOnly ? ' (base only)' : ''}`;
  const scheduleLabel = describeSchedule(rule.schedule);
  const tt = rule.taxTreatment;

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={`text-sm font-semibold ${
                rule.enabled
                  ? 'text-ink-900 dark:text-ink-50'
                  : 'text-ink-400 line-through'
              }`}
            >
              {rule.name}
            </span>
            {tt && (
              <Chip tone={TAX_TREATMENT_TONES[tt]} size="sm">
                <GlossaryTerm term={TAX_TREATMENT_GLOSSARY[tt]}>
                  {TAX_TREATMENT_LABELS[tt]}
                </GlossaryTerm>
              </Chip>
            )}
            {!rule.enabled && (
              <Chip tone="muted" size="sm">
                disabled
              </Chip>
            )}
          </div>
          <div className="mt-1 text-[11px] text-ink-500 dark:text-ink-300">
            {targetLabel} · {modeLabel} · {scheduleLabel}
          </div>
          {rule.notes && (
            <div className="mt-1 text-[11px] italic text-ink-400 dark:text-ink-400">
              {rule.notes}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-ink-400">
            Effective annual
          </div>
          <div className="font-mono text-sm font-semibold tabular-nums">
            {fmtMoney(actual)}
          </div>
          {capped > 0.5 && (
            <div className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
              −{fmtMoney(capped)} trimmed
              {capReason && ` (${capReason})`}
            </div>
          )}
          {capped <= 0.5 && intended !== actual && (
            <div className="mt-0.5 text-[10px] text-ink-400">
              intended {fmtMoney(intended)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="btn btn-ghost text-xs"
        >
          Edit
        </button>
      </div>
    </li>
  );
}

function RuleEditor({
  rule,
  cap,
  accounts,
  isNew,
  headroomBefore,
  capLabel,
  onCancel,
  onSave,
  onDelete,
}: {
  rule: ContributionRule;
  cap: CapId;
  accounts: Account[];
  isNew: boolean;
  /** Headroom in this cap's cap BEFORE this rule's intended dollars are
   *  counted. For new rules this equals current cap headroom; for edits
   *  it's headroom + the existing rule's contribution (so the user can
   *  decrease/increase without seeing a phantom over-cap warning). */
  headroomBefore: number;
  /** The cap dollar value, or null for the uncapped cap. Used in the
   *  live-headroom hint copy. */
  capLabel: number | null;
  onCancel: () => void;
  onSave: (r: ContributionRule) => void;
  onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<ContributionRule>(rule);
  const [errors, setErrors] = useState<string[]>([]);

  const investableAccounts = accounts.filter(a => a.bucket !== null && !a.archived);

  // Pre-scoped tax-treatment options for this cap.
  const allowedTreatments = CAP_TREATMENTS[cap];

  // Live intended-amount preview from the draft. Drives the headroom hint
  // and the over-cap warning. Reuses the same resolveRuleAnnual the engine
  // uses, fed a synthetic comp matching the editor's numeric mode.
  const draftIntended = useMemo(() => {
    // We can't reach Compensation here without prop-drilling; for fixed_dollar
    // mode we know the annual exactly. For percent_of_pay we punt on the
    // hint (the page-level cap-aware summary still reflects the real number).
    if (draft.mode.kind === 'fixed_dollar') return Math.max(0, draft.mode.annual);
    return 0; // hint suppressed for percent rules in the editor
  }, [draft.mode]);

  // Headroom AFTER this draft, given headroomBefore and draft's intended.
  const headroomAfter = headroomBefore - draftIntended;
  const overCap = capLabel !== null && headroomAfter < -0.5;

  function validate(r: ContributionRule): string[] {
    const errs: string[] = [];
    if (!r.name.trim()) errs.push('Name is required.');
    if (r.mode.kind === 'fixed_dollar' && !(r.mode.annual > 0)) {
      errs.push('Fixed-dollar amount must be > 0.');
    }
    if (r.mode.kind === 'percent_of_pay' && !(r.mode.pct > 0)) {
      errs.push('Percent must be > 0.');
    }
    if (r.target.kind === 'account' && !r.target.accountId) {
      errs.push('Pick a target account.');
    }
    return errs;
  }

  function commit() {
    const errs = validate(draft);
    setErrors(errs);
    if (errs.length === 0) onSave(draft);
  }

  const targetSelectValue =
    draft.target.kind === 'bucket'
      ? `bucket:${draft.target.bucket}`
      : `account:${draft.target.accountId}`;

  // For the headroom hint copy.
  const capName: Record<CapId, string> = {
    '402g': '402(g)',
    '415c': '415(c)',
    ira: 'IRA',
    uncapped: '',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {isNew ? `New rule in ${capName[cap] || 'uncapped'} cap` : `Edit ${rule.name || 'rule'}`}
        </h3>
        <Toggle
          checked={draft.enabled}
          onChange={v => setDraft({ ...draft, enabled: v })}
          ariaLabel="Enabled"
          size="sm"
        />
      </div>

      <div>
        <label className="label">Name</label>
        <input
          className="input mt-1"
          value={draft.name}
          placeholder="e.g. 401(k) employee pretax"
          onChange={e => setDraft({ ...draft, name: e.target.value })}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Target</label>
          <select
            className="input mt-1"
            value={targetSelectValue}
            onChange={e => {
              const [kind, arg] = e.target.value.split(':');
              if (kind === 'bucket') {
                setDraft({
                  ...draft,
                  target: {
                    kind: 'bucket',
                    bucket: arg as 'hysa' | 'taxable' | 'retirement',
                  },
                });
              } else {
                setDraft({
                  ...draft,
                  target: { kind: 'account', accountId: arg },
                });
              }
            }}
          >
            <optgroup label="Buckets">
              <option value="bucket:hysa">HYSA bucket</option>
              <option value="bucket:taxable">Taxable bucket</option>
              <option value="bucket:retirement">Retirement bucket</option>
            </optgroup>
            {investableAccounts.length > 0 && (
              <optgroup label="Specific account">
                {investableAccounts.map(a => (
                  <option key={a.id} value={`account:${a.id}`}>
                    {a.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="label">Tax treatment</label>
          <select
            className="input mt-1"
            value={draft.taxTreatment ?? ''}
            onChange={e => {
              const v = e.target.value;
              setDraft({
                ...draft,
                taxTreatment: v === '' ? undefined : (v as ContributionTaxTreatment),
              });
            }}
          >
            {cap === 'uncapped' && (
              <option value="">— None (HYSA / non-retirement) —</option>
            )}
            {allowedTreatments.map(t => (
              <option key={t} value={t}>
                {TAX_TREATMENT_LABELS[t]} — {TAX_TREATMENT_CAP_HINT[t]}
              </option>
            ))}
          </select>
          <div className="mt-1 text-[10px] text-ink-400">
            Options scoped to this cap. Change cap via Cancel + a different
            "Add" button.
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Mode</label>
          <select
            className="input mt-1"
            value={draft.mode.kind}
            onChange={e => {
              const k = e.target.value as ContributionMode['kind'];
              if (k === 'fixed_dollar') {
                setDraft({ ...draft, mode: { kind: 'fixed_dollar', annual: 0 } });
              } else {
                setDraft({ ...draft, mode: { kind: 'percent_of_pay', pct: 0 } });
              }
            }}
          >
            <option value="fixed_dollar">Fixed dollar amount</option>
            <option value="percent_of_pay">Percent of pay</option>
          </select>
        </div>
        <div>
          {draft.mode.kind === 'fixed_dollar' ? (
            <>
              <label className="label">Annual amount</label>
              <div className="mt-1">
                <MoneyInput
                  value={draft.mode.annual}
                  onChange={v =>
                    setDraft({ ...draft, mode: { kind: 'fixed_dollar', annual: v } })
                  }
                />
              </div>
            </>
          ) : (
            <>
              <label className="label">Percent of pay</label>
              <div className="mt-1">
                <PctInput
                  value={draft.mode.pct}
                  onChange={v =>
                    setDraft({
                      ...draft,
                      mode: {
                        kind: 'percent_of_pay',
                        pct: v,
                        baseOnly: draft.mode.kind === 'percent_of_pay' ? draft.mode.baseOnly : undefined,
                      },
                    })
                  }
                  decimals={2}
                />
              </div>
              <label className="mt-2 flex items-center gap-2 text-[11px] text-ink-500 dark:text-ink-300">
                <input
                  type="checkbox"
                  checked={draft.mode.kind === 'percent_of_pay' && !!draft.mode.baseOnly}
                  onChange={e =>
                    setDraft({
                      ...draft,
                      mode: {
                        kind: 'percent_of_pay',
                        pct: draft.mode.kind === 'percent_of_pay' ? draft.mode.pct : 0,
                        baseOnly: e.target.checked || undefined,
                      },
                    })
                  }
                />
                Base salary only (exclude bonus)
              </label>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Schedule</label>
          <select
            className="input mt-1"
            value={draft.schedule.kind}
            onChange={e => {
              const k = e.target.value as ContributionSchedule['kind'];
              if (k === 'lump_sum') {
                setDraft({ ...draft, schedule: { kind: 'lump_sum', month: 1 } });
              } else if (k === 'monthly') {
                setDraft({ ...draft, schedule: { kind: 'monthly' } });
              } else if (k === 'with_bonus') {
                setDraft({ ...draft, schedule: { kind: 'with_bonus' } });
              } else {
                setDraft({ ...draft, schedule: { kind: 'with_paycheck' } });
              }
            }}
          >
            <option value="monthly">Monthly</option>
            <option value="with_paycheck">With paycheck (≈ monthly)</option>
            <option value="lump_sum">Lump sum (specific month)</option>
            <option value="with_bonus">With bonus</option>
          </select>
        </div>
        {draft.schedule.kind === 'lump_sum' && (
          <div>
            <label className="label">Month</label>
            <select
              className="input mt-1"
              value={draft.schedule.month}
              onChange={e =>
                setDraft({
                  ...draft,
                  schedule: { kind: 'lump_sum', month: Number(e.target.value) as PayMonth },
                })
              }
            >
              {MONTH_LABELS.map((label, idx) => (
                <option key={label} value={idx + 1}>
                  {idx + 1} — {label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="label">Notes (optional)</label>
        <input
          className="input mt-1"
          value={draft.notes ?? ''}
          onChange={e =>
            setDraft({ ...draft, notes: e.target.value || undefined })
          }
        />
      </div>

      {/* Live cap headroom hint — only meaningful for capped caps and
          fixed_dollar mode (we don't have salary in scope for percent). */}
      {capLabel !== null && draft.mode.kind === 'fixed_dollar' && (
        <div
          className={`rounded-lg px-3 py-2 text-[11px] ${
            overCap
              ? 'bg-amber-50 text-amber-800 dark:bg-amber-700/15 dark:text-amber-300'
              : 'bg-ink-50/60 text-ink-600 dark:bg-ink-800/50 dark:text-ink-300'
          }`}
        >
          {overCap ? (
            <>
              ⚠ This will exceed the {capName[cap]} cap by{' '}
              <strong>{fmtMoney(-headroomAfter)}</strong>. The engine will
              auto-trim it. Consider routing the spillover to a different cap
              (e.g. {cap === '402g' ? 'mega backdoor → Roth' : 'after-tax brokerage'}).
            </>
          ) : (
            <>
              After this rule, your {capName[cap]} cap will be{' '}
              <strong>
                {capLabel > 0 ? Math.round(((capLabel - headroomAfter) / capLabel) * 100) : 0}% used
              </strong>{' '}
              ({fmtMoney(capLabel - headroomAfter)} / {fmtMoney(capLabel)}).
            </>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <Banner tone="danger" title="Fix these before saving">
          <ul className="list-disc pl-4">
            {errors.map(e => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Banner>
      )}

      <div className="flex flex-wrap gap-2">
        <button className="btn btn-ghost flex-1" onClick={onCancel}>
          Cancel
        </button>
        {onDelete && (
          <button className="btn btn-danger" onClick={onDelete}>
            Delete
          </button>
        )}
        <button className="btn btn-primary flex-1" onClick={commit}>
          {isNew ? 'Create rule' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/** Confirmation panel for the Quick Setup wizard. Shows the three rules
 *  Cairn would create with editable amounts before commit. */
function QuickSetupConfirm({
  rules,
  onCancel,
  onConfirm,
}: {
  rules: ContributionRule[];
  onCancel: () => void;
  onConfirm: (rules: ContributionRule[]) => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<ContributionRule[]>(rules);

  function setAmount(idx: number, annual: number) {
    setDraft(prev =>
      prev.map((r, i) =>
        i === idx
          ? { ...r, mode: { kind: 'fixed_dollar', annual: Math.max(0, annual) } }
          : r,
      ),
    );
  }

  return (
    <div className="border-t border-ink-100 p-4 dark:border-ink-700">
      <div className="text-sm font-semibold">
        Confirm — Cairn will add these {draft.length} rules
      </div>
      <div className="mt-1 text-[11px] text-ink-500 dark:text-ink-300">
        Amounts are editable. Each rule lands in its cap at the IRS cap.
      </div>
      <ul className="mt-3 space-y-3">
        {draft.map((r, idx) => (
          <li
            key={r.id}
            className="grid grid-cols-1 gap-2 rounded-xl bg-ink-50/60 p-3 dark:bg-ink-800/40 md:grid-cols-[1fr_auto]"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold">{r.name}</div>
              <div className="text-[11px] text-ink-500 dark:text-ink-300">
                {r.taxTreatment && (
                  <Chip tone={TAX_TREATMENT_TONES[r.taxTreatment]} size="sm">
                    {TAX_TREATMENT_LABELS[r.taxTreatment]}
                  </Chip>
                )}{' '}
                · {describeSchedule(r.schedule)} · →{' '}
                {r.target.kind === 'bucket' ? `${capitalize(r.target.bucket)} bucket` : 'account'}
              </div>
            </div>
            <div className="md:w-40">
              <label className="label">Annual amount</label>
              <div className="mt-1">
                <MoneyInput
                  value={r.mode.kind === 'fixed_dollar' ? r.mode.annual : 0}
                  onChange={v => setAmount(idx, v)}
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap gap-2">
        <button className="btn btn-ghost flex-1" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn btn-primary flex-1"
          onClick={() => void onConfirm(draft)}
        >
          Add {draft.length} rules
        </button>
      </div>
    </div>
  );
}

interface AccountInvestmentRowProps {
  account: Account;
  balance: number;
  rules: ContributionRule[];
  intended: number;
  actual: number;
  touches: { date: string; value: number; createdAt: string }[];
  year1End: number | null;
  onSetTaxSleeve: (sleeve: 'pretax' | 'roth') => Promise<void>;
}

function AccountInvestmentRow({
  account,
  balance,
  rules,
  intended,
  actual,
  touches,
  year1End,
  onSetTaxSleeve,
}: AccountInvestmentRowProps) {
  const showSleeve = account.bucket === 'retirement';
  const sleeve = account.taxSleeve ?? 'pretax';

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              {account.name}
            </span>
            <Chip tone="muted" size="sm">
              {account.bucket}
            </Chip>
            {showSleeve && (
              <Chip tone={sleeve === 'roth' ? 'pos' : 'accent'} size="sm">
                {sleeve === 'roth' ? 'Roth sleeve' : 'Pretax sleeve'}
              </Chip>
            )}
          </div>
          {rules.length > 0 ? (
            <div className="mt-1 text-[11px] text-ink-500 dark:text-ink-300">
              Fed by:{' '}
              {rules.map((r, i) => (
                <span key={r.id}>
                  {i > 0 && ', '}
                  <span className={r.enabled ? '' : 'text-ink-400 line-through'}>
                    {r.name}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <div className="mt-1 text-[11px] italic text-ink-400">
              No rules target this account directly.
            </div>
          )}
          {showSleeve && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-ink-500 dark:text-ink-300">
              <span>Tax sleeve:</span>
              <button
                type="button"
                onClick={() => void onSetTaxSleeve('pretax')}
                className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                  sleeve === 'pretax'
                    ? 'bg-accent-500 text-white'
                    : 'bg-ink-100 text-ink-700 dark:bg-ink-700 dark:text-ink-100'
                }`}
              >
                Pretax
              </button>
              <button
                type="button"
                onClick={() => void onSetTaxSleeve('roth')}
                className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                  sleeve === 'roth'
                    ? 'bg-pos-500 text-white'
                    : 'bg-ink-100 text-ink-700 dark:bg-ink-700 dark:text-ink-100'
                }`}
              >
                Roth
              </button>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-ink-400">
            Current balance
          </div>
          <Money value={balance} size="lg" />
          {touches.length >= 2 && (
            <div className="mt-1 flex justify-end">
              <SimpleSparkline points={touches} />
            </div>
          )}
        </div>
      </div>
      {(actual > 0 || intended > 0 || year1End !== null) && (
        <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl bg-ink-50/60 p-3 text-[11px] dark:bg-ink-800/50 md:grid-cols-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-ink-400">
              Effective annual contribution
            </div>
            <div className="mt-0.5 font-mono text-xs font-semibold tabular-nums">
              {fmtMoney(actual)}
            </div>
            {Math.abs(intended - actual) > 0.5 && (
              <div className="text-[10px] text-amber-600 dark:text-amber-400">
                intended {fmtMoney(intended)}
              </div>
            )}
          </div>
          {year1End !== null && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-ink-400">
                Bucket end of yr 1
              </div>
              <div className="mt-0.5 font-mono text-xs font-semibold tabular-nums">
                {fmtMoney(year1End)}
              </div>
              <div className="text-[10px] text-ink-400">
                across {account.bucket} bucket
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-ink-400">
              Last touched
            </div>
            <div className="mt-0.5 text-xs text-ink-700 dark:text-ink-100">
              {touches.length > 0 ? touches.at(-1)!.date : '—'}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function RsuVestRowEditor({
  vest,
  startingYear,
  onChange,
  onRemove,
}: {
  vest: RsuVestRow;
  startingYear: number;
  onChange: (v: RsuVestRow) => void;
  onRemove: () => void;
}) {
  const calendarYear = startingYear + vest.yearOffset;
  const monthLabel = MONTH_LABELS[vest.month - 1];
  return (
    <li className="grid grid-cols-1 gap-3 px-4 py-3 md:grid-cols-[1fr_auto_auto_auto_auto]">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">
          {monthLabel} {calendarYear}
        </span>
        <span className="text-[11px] text-ink-500 dark:text-ink-300">
          Year offset {vest.yearOffset} · {fmtMoney(vest.grossValue)} gross
        </span>
      </div>
      <div>
        <label className="label">Year offset</label>
        <input
          type="number"
          className="input mt-1 w-20 text-right font-mono tabular-nums"
          value={vest.yearOffset}
          min={0}
          onChange={e =>
            onChange({ ...vest, yearOffset: Math.max(0, Number(e.target.value)) })
          }
        />
      </div>
      <div>
        <label className="label">Month</label>
        <select
          className="input mt-1 w-24"
          value={vest.month}
          onChange={e => onChange({ ...vest, month: Number(e.target.value) as PayMonth })}
        >
          {MONTH_LABELS.map((label, idx) => (
            <option key={label} value={idx + 1}>
              {idx + 1} — {label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="label">Gross value</label>
        <div className="mt-1 w-32">
          <MoneyInput
            value={vest.grossValue}
            onChange={v => onChange({ ...vest, grossValue: v })}
          />
        </div>
      </div>
      <div className="flex items-end">
        <button className="btn btn-ghost text-xs text-neg-700 dark:text-neg-500" onClick={onRemove}>
          Remove
        </button>
      </div>
    </li>
  );
}

/**
 * Time-honest sparkline: x is real-time position between snapshots, dots
 * mark each actual sample so an uneven cadence is visible instead of
 * being smoothed into evenly-spaced steps.
 */
function SimpleSparkline({ points }: { points: { date: string; value: number }[] }) {
  if (points.length < 2) return null;
  const w = 100;
  const h = 20;
  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const times = points.map(p => new Date(p.date).getTime());
  const tMin = times[0];
  const tMax = times[times.length - 1];
  const tSpan = tMax - tMin || 1;
  const xy = points.map((p, i) => ({
    x: ((times[i] - tMin) / tSpan) * w,
    y: h - ((p.value - min) / range) * h,
  }));
  const path = xy
    .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`)
    .join(' ');
  return (
    <svg
      className="h-5 w-24"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-label="balance trend"
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        className="text-accent-500"
      />
      {xy.map((pt, i) => (
        <circle
          key={i}
          cx={pt.x}
          cy={pt.y}
          r={1}
          fill="currentColor"
          vectorEffect="non-scaling-stroke"
          className="text-accent-500"
        />
      ))}
    </svg>
  );
}

// ── Pure helpers ───────────────────────────────────────────────────────────

/** Build a blank rule pre-scoped to a cap. Used when "+ Add" is clicked
 *  inside a cap card without a prefill. */
function blankRuleForCap(
  order: number,
  cap: CapId,
  prefill?: Partial<ContributionRule> | null,
): ContributionRule {
  const defaults: Record<CapId, Pick<ContributionRule, 'taxTreatment' | 'schedule' | 'target'>> = {
    '402g': {
      taxTreatment: 'pretax',
      schedule: { kind: 'lump_sum', month: 1 },
      target: { kind: 'bucket', bucket: 'retirement' },
    },
    '415c': {
      taxTreatment: 'mega_backdoor_to_roth',
      schedule: { kind: 'lump_sum', month: 1 },
      target: { kind: 'bucket', bucket: 'retirement' },
    },
    ira: {
      taxTreatment: 'roth_ira',
      schedule: { kind: 'monthly' },
      target: { kind: 'bucket', bucket: 'retirement' },
    },
    uncapped: {
      taxTreatment: 'after_tax_brokerage',
      schedule: { kind: 'monthly' },
      target: { kind: 'bucket', bucket: 'taxable' },
    },
  };
  const d = defaults[cap];
  return {
    id: uuid(),
    name: prefill?.name ?? '',
    target: prefill?.target ?? d.target,
    mode: prefill?.mode ?? { kind: 'fixed_dollar', annual: 0 },
    schedule: prefill?.schedule ?? d.schedule,
    taxTreatment: prefill?.taxTreatment ?? d.taxTreatment,
    enabled: prefill?.enabled ?? true,
    order,
  } as ContributionRule;
}

function describeSchedule(s: ContributionSchedule): string {
  switch (s.kind) {
    case 'monthly':
      return 'Monthly';
    case 'with_paycheck':
      return 'With paycheck';
    case 'lump_sum':
      return `Lump sum — ${MONTH_LABELS[s.month - 1]}`;
    case 'with_bonus':
      return 'With bonus';
  }
}

function capitalize(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}
